import React, { useEffect, useRef, useState } from "react";
import { Maximize2, X, Share2, Target, Palette, ArrowRight, Volume2 } from "lucide-react";
import { AiIcon } from "@/components/ui/ai-icon";
import { playPreferSound, rememberMuteChoice, unmuteNow } from "@/lib/demoVideoSound";

const VIDEO_VERSION = "2026-09-28-music";
const SRC = `/videos/app-demo.mp4?v=${VIDEO_VERSION}`;
const POSTER = `/videos/app-demo-poster.jpg?v=${VIDEO_VERSION}`;

// Landing "See it in action" (2026-09-28, replacing the tablet/mobile
// DeviceShowcase mockup: "embed the video ... user can see the video, the
// overlay float video, without leaving the page"). The real product demo
// (scripts/demo-video/record.py) plays muted inline once the section is on
// screen; if it's playing and the visitor scrolls past, the SAME <video>
// element floats as a corner mini-player (so playback never restarts).
// Expand opens the full DemoModal overlay; Share goes to /demo.
export function DemoVideoSection({ isLight, onExpand, onStart }) {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Autoplay when the section is on screen -- with sound when the browser
  // allows it (see lib/demoVideoSound.js), otherwise muted until the
  // visitor's first click/key press.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !inView || reduceMotion || dismissed || !v.paused) return;
    playPreferSound(v);
  }, [inView, reduceMotion, dismissed]);

  const floating = playing && !inView && !dismissed;

  const closeFloat = () => {
    setDismissed(true);
    videoRef.current?.pause();
  };
  const expand = () => {
    videoRef.current?.pause();
    onExpand?.();
  };

  const muted = isLight ? "text-[#6B8578]" : "text-[#9FB8AC]";
  return (
    <section
      ref={sectionRef}
      data-testid="landing-demo-section"
      className={`border-y py-24 ${isLight ? "border-black/5 bg-[#FAFAF9]" : "border-white/10 bg-[#0F211B]"}`}
    >
      <div className="mx-auto max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A67C52]">See it in action</p>
          <h2 className={`mt-3 text-3xl font-['Manrope'] font-bold md:text-5xl ${isLight ? "text-[#3D9970]" : "text-white"}`}>
            From job posting to finished application.
          </h2>
          <p className={`mt-4 text-base sm:text-lg ${muted}`}>30 seconds of the real product: paste a job, click once, and get a tailored resume, cover letter and ATS report.</p>
        </div>

        {/* Placeholder keeps the section's height while the player floats. */}
        <div className="relative mx-auto mt-10 max-w-5xl" style={{ aspectRatio: "16 / 9" }}>
          <div
            data-testid="landing-demo-player"
            className={
              floating
                // Bottom-LEFT: the support-chat bubble owns bottom-right.
                ? "fixed bottom-4 left-4 z-[60] w-[260px] sm:w-[340px] rounded-xl overflow-hidden shadow-2xl shadow-black/50 ring-1 ring-white/15 bg-black"
                : `absolute inset-0 rounded-2xl overflow-hidden shadow-2xl ring-1 ${isLight ? "ring-black/10" : "ring-white/10"} bg-black`
            }
          >
            <video
              ref={videoRef}
              data-testid="landing-demo-video"
              className={`block w-full ${floating ? "aspect-video" : "h-full object-cover"}`}
              src={SRC}
              poster={POSTER}
              muted
              loop
              playsInline
              preload="none"
              controls={!floating}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onVolumeChange={(e) => { setIsMuted(e.currentTarget.muted); rememberMuteChoice(e); }}
            />
            {playing && isMuted && (
              <button
                type="button"
                data-testid="landing-demo-unmute"
                onClick={() => unmuteNow(videoRef.current)}
                className={`absolute ${floating ? "bottom-1.5 left-1.5 px-2 py-1 text-[10px]" : "top-3 left-3 px-3 py-1.5 text-xs"} inline-flex items-center gap-1.5 rounded-full bg-black/60 font-bold text-white backdrop-blur hover:bg-[#A67C52]`}
              >
                <Volume2 className="size-3.5" /> Sound on
              </button>
            )}
            {floating && (
              <div className="absolute top-1.5 right-1.5 flex gap-1">
                <button type="button" data-testid="landing-demo-float-expand" onClick={expand} aria-label="Expand demo" className="grid size-7 place-items-center rounded-full bg-black/60 text-white hover:bg-[#A67C52]">
                  <Maximize2 className="size-3.5" />
                </button>
                <button type="button" data-testid="landing-demo-float-close" onClick={closeFloat} aria-label="Close mini player" className="grid size-7 place-items-center rounded-full bg-black/60 text-white hover:bg-[#A67C52]">
                  <X className="size-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {[
            { icon: Target, label: "Paste the job" },
            { icon: AiIcon, label: "One click" },
            { icon: Palette, label: "Download your bundle" },
          ].map(({ icon: Icon, label }, i) => (
            <span key={label} className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-bold ${isLight ? "border-black/10 bg-white text-[#3D9970]" : "border-white/10 bg-white/[0.04] text-[#E3EFE9]"}`}>
              <span className="text-[#A67C52]">{i + 1}</span> <Icon className="size-3.5 text-[#A67C52]" /> {label}
            </span>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <button type="button" onClick={onStart} data-testid="landing-demo-start" className="inline-flex items-center gap-2 rounded-full bg-[#A67C52] px-6 py-3 text-sm font-bold text-white hover:bg-[#8A6640] transition-colors">
            Build yours free <ArrowRight className="size-4" />
          </button>
          <button type="button" onClick={expand} data-testid="landing-demo-expand" className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-bold transition-colors ${isLight ? "border-black/15 text-[#3D9970] hover:border-black/40" : "border-white/20 text-white hover:border-[#A67C52]"}`}>
            <Maximize2 className="size-4" /> Watch full screen
          </button>
          <a href="/demo" data-testid="landing-demo-share" className={`inline-flex items-center gap-1.5 px-3 py-3 text-sm font-bold ${muted} hover:text-[#A67C52]`}>
            <Share2 className="size-4" /> Share
          </a>
        </div>
      </div>
    </section>
  );
}
